/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { useCallback, useRef } from "react";
import { Button, IButtonProps } from "../Button/Button";
import { Icon, ICON_SIZE } from "../Icon";
import { IDragHandleProps } from "../DragAndDrop/DragAndDropTypes";
import { Tooltip } from "../Tooltip";
import { FC_ANCHOR } from "../FloatingContainer";

export interface IFieldListItemAction extends IButtonProps {}

export enum FIELD_LIST_ITEM_ALIGN {
	LEFT = "left",
	CENTER = "center",
	RIGHT = "right"
}

export interface IFieldListItemProps extends React.ComponentPropsWithRef<"div"> {
	label?: string;
	leftIcon?: string;
	rightIcon?: string;
	actions?: IFieldListItemAction[];
	tooltip?: string;
	isActive?: boolean;
	align?: FIELD_LIST_ITEM_ALIGN;
	className?: string;
	onClick?: () => void;
	disabled?: boolean;
	readOnly?: boolean;
	dragHandleProps?: IDragHandleProps;
}

export const FieldListItem = React.forwardRef<HTMLDivElement, IFieldListItemProps>(
	(
		{
			label,
			leftIcon,
			rightIcon,
			actions,
			tooltip,
			isActive,
			align,
			className,
			onClick,
			children,
			disabled,
			readOnly,
			dragHandleProps,
			...props
		},
		ref
	) => {
		const buttonElRef = useRef<HTMLButtonElement>();

		const handleButtonClick = useCallback(() => {
			if (onClick) {
				onClick();
			}
		}, [ onClick ]);

		const baseClass = BEM("edt-field-list-item", null, {
			"is-active": isActive,
			disabled,
			align: align || FIELD_LIST_ITEM_ALIGN.LEFT,
			readonly: readOnly
		});

		if (className) {
			baseClass.add(className);
		}

		return (
			<div className={baseClass.toClassName()} ref={ref} {...props}>
				<button
					className={baseClass.element("button").toClassName()}
					onClick={handleButtonClick}
					ref={buttonElRef}
					disabled={disabled || readOnly}
				>
					{dragHandleProps && (
						<div className={baseClass.element("handle").toClassName()} {...dragHandleProps}>
							<Icon name="mdi/drag" size={ICON_SIZE.MEDIUM} />
						</div>
					)}
					{leftIcon ? <Icon name={leftIcon} size={ICON_SIZE.XSMALL} /> : null}
					{label ? <span className={baseClass.element("label").toClassName()}>{label}</span> : null}
					{rightIcon ? <Icon name={rightIcon} size={ICON_SIZE.XSMALL} /> : null}
					{tooltip ? (
						<Tooltip for={buttonElRef} anchor={FC_ANCHOR.CENTER_TOP} openDelay={1200}>
							{tooltip}
						</Tooltip>
					) : null}
				</button>
				{Array.isArray(actions) &&
					actions.map((action, index) => {
						const actionClass = BEM("edt-field-list-item-action");
						return <Button key={index} {...action} baseClass={actionClass} disabled={readOnly} />;
					})}
				{children}
			</div>
		);
	}
);
